import { useState } from "react";
import { api } from "./api";
import { Modal, Working } from "./ui";

export type FlagKind = "bank_line" | "purchase" | "stock_lot";

/**
 * A flag for review (D, 14 October 2026): "when I flag the transaction or expense or
 * inventory, I know that it is something that I am watching out for. When it is flagged,
 * it will be at the top. When unflagged, it will go back to its original position."
 */
export function FlagButton({
  kind,
  id,
  flagged,
  note,
  onChanged,
}: {
  readonly kind: FlagKind;
  readonly id: string;
  readonly flagged: boolean;
  readonly note?: string | null | undefined;
  readonly onChanged: () => void;
}): React.ReactElement {
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(flagged);
  const [writing, setWriting] = useState<string | undefined>();
  const on = busy ? shown : flagged;
  return (
    <>
    <button
      type="button"
      aria-pressed={on}
      aria-label={on ? "Remove the review flag" : "Flag for review"}
      title={on ? `Flagged for review${note ? `: ${note}` : ""}. Click to remove the flag.` : "Flag for review. Flagged items stay at the top of the list."}
      className={`inline-flex items-center justify-center w-6 h-6 rounded-sm shrink-0 ${on ? "text-danger" : "text-[#c4c8cf] hover:text-danger dark:text-white/30"}`}
      disabled={busy}
      onClick={(event) => {
        event.stopPropagation();
        setShown(!flagged);
        setBusy(true);
        void api
          .setFlag(kind, id, !flagged)
          .then(onChanged)
          .catch(() => setShown(flagged))
          .finally(() => setBusy(false));
      }}
    >
      <i className={on ? "ri-flag-fill" : "ri-flag-line"} />
    </button>
    {/* A note on the flag (D, 15 October 2026: "a small button for me to write down some manual notes"). */}
    {on ? (
      <button
        type="button"
        aria-label={note ? "Change the note on the flag" : "Write a note on the flag"}
        title={note ? `Note: ${note}` : "Write a note"}
        className={`inline-flex items-center justify-center w-6 h-6 rounded-sm shrink-0 ${note ? "text-danger" : "text-[#8c9097] hover:text-danger"}`}
        onClick={(event) => {
          event.stopPropagation();
          setWriting(note ?? "");
        }}
      >
        <i className={note ? "ri-sticky-note-fill" : "ri-sticky-note-add-line"} />
      </button>
    ) : null}
    {writing !== undefined ? (
      <span onClick={(event) => event.stopPropagation()}>
        <Modal
          open
          title="Note on the flag"
          onClose={() => setWriting(undefined)}
          footer={
            <>
              <button type="button" className="ti-btn ti-btn-light" onClick={() => setWriting(undefined)}>
                Cancel
              </button>
              <button
                type="button"
                className="ti-btn ti-btn-primary-full"
                disabled={busy}
                onClick={() => {
                  setBusy(true);
                  void api
                    .setFlag(kind, id, true, writing.trim())
                    .then(() => {
                      setWriting(undefined);
                      onChanged();
                    })
                    .finally(() => setBusy(false));
                }}
              >
                {busy ? <Working label="Saving" /> : "Save note"}
              </button>
            </>
          }
        >
          <textarea
            rows={4}
            maxLength={500}
            autoFocus
            className="form-control"
            aria-label="Note"
            placeholder="What to check, e.g. Ask Daren which invoice this was"
            value={writing}
            onChange={(event) => setWriting(event.target.value)}
          />
        </Modal>
      </span>
    ) : null}
    </>
  );
}

/** The note on a flag, small, under the row's first column. */
export function FlagNote({ note }: { readonly note?: string | null | undefined }): React.ReactElement | null {
  return note ? <span className="block text-danger text-[0.6875rem] whitespace-normal break-words max-w-[16rem] mt-0.5">{note}</span> : null;
}

/** Flagged first, everything else in the order it already had. */
export function flaggedFirst<T extends { readonly flagged?: boolean | undefined }>(items: readonly T[]): T[] {
  return [...items.filter((item) => item.flagged), ...items.filter((item) => !item.flagged)];
}
